import {
  FormStep,
  ListingFormValues,
} from "@/components/listings/filters/FilterSchema";
import { z } from "zod"; // Import Zod
import { Listing } from "@/lib/types"; // Import Listing type
import { pricePeriodOptions } from "@/lib/data/propertyTypes"; // Import pricePeriodOptions

// Define consts for enum-like string arrays for listing_type
const listingTypes = ["sale", "rent", "lease"] as const;

export const finalReviewSchema = z
  .object({
    title: z
      .string()
      .min(3, "Title must be at least 3 characters")
      .max(200, "Title must be less than 200 characters"),
    description: z
      .string()
      .min(10, "Description must be at least 10 characters")
      .max(5000, "Description must be less than 5000 characters"),
    property_type_id: z
      .number({ invalid_type_error: "Property type must be a valid selection" })
      .int()
      .min(1, "Property type is required"),
    listing_type: z.enum(listingTypes, {
      required_error: "Listing type is required",
    }),
    price: z
      .number({ required_error: "Price is required" })
      .min(0, "Price must be a non-negative number"),
    currency: z
      .string()
      .max(3, "Currency code must be 3 characters")
      .optional()
      .default("LKR"),
    price_period: z.string().min(1, "Price period is required").optional(),
    location_id: z
      .number({ invalid_type_error: "Location must be a valid selection" })
      .int()
      .min(1, "Location (city) is required"),
    address: z
      .string()
      .max(500, "Address must be less than 500 characters")
      .optional(),

    // Optional fields from backend schema
    size: z.number().nullable().optional(),
    size_unit: z.string().optional(),
    bedrooms: z.number().nullable().optional(),
    bathrooms: z.number().nullable().optional(), // Changed to nullable
    parking_spaces: z.number().nullable().optional(), // Changed to nullable
    latitude: z.number().nullable().optional(), // Changed to nullable
    longitude: z.number().nullable().optional(), // Changed to nullable
    amenities: z.array(z.number()).optional(),
    features: z.array(z.string()).optional(),
    plot_size: z.number().nullable().optional(), // Changed to nullable
    floor_size: z.number().nullable().optional(), // Changed to nullable
    furnishing: z.string().nullable().optional(), // Changed to nullable
    rooms: z.number().nullable().optional(), // Changed to nullable
    num_floors: z.number().nullable().optional(), // Changed to nullable
    yearBuilt: z.number().nullable().optional(), // Changed to yearBuilt to match frontend and backend validation
    availability_status: z.string().nullable().optional(), // Changed to nullable
    advance_payment_months: z.number().nullable().optional(), // Changed to nullable
    minimum_term_months: z.number().nullable().optional(), // Changed to nullable
    minimumTermUnit: z.enum(["months", "years"]).optional().default("months"),
    deposit_months: z.number().nullable().optional(), // Changed to nullable
    short_term_available: z.boolean().nullable().optional(), // Changed to nullable
    bills_included: z.boolean().nullable().optional(), // Changed to nullable

    // Contact fields (optional in backend, but good to include if collected)
    contact_name: z.string().optional(),
    contact_phone: z.string().optional(),
    contact_email: z.string().email("Invalid email address").optional(),
    contact_whatsapp: z.boolean().optional(),
    contact_botim: z.boolean().optional(),
    hide_contact: z.boolean().optional(),

    // Images are handled separately, not typically part of this direct data validation for publish payload
    // images: z.array(z.object({ id: z.union([z.string(), z.number()]), url: z.string(), isPrimary: z.boolean().optional() })).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.listing_type !== "sale" &&
      (!data.price_period || data.price_period.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price period is required for rent/lease listings",
        path: ["price_period"],
      });
    }
  });

export const getStepTabValue = (step: FormStep): string => {
  return `step-${step}`;
};

export const getFieldsForStep = (step: FormStep): string[] => {
  switch (step) {
    case FormStep.PropertyInformation:
      return [
        "propertyType",
        "listingType",
        "price",
        "currency",
        "pricePeriod",
      ];
    case FormStep.PropertyDetails:
      return [
        "size",
        "size_unit",
        "floorSizeSqFt",
        "plotSizeSqft",
        "rooms",
        "bedrooms",
        "bathrooms",
        "parkings",
        "floors",
        "furnishing",
        "status",
        "advancePayment",
        "minimumTermValue",
        "minimumTermUnit",
        "depositRequired",
        "shortTermAvailable",
        "billsIncluded",
        "features",
        "amenities",
      ];
    case FormStep.Location:
      return [
        "location.city_id",
        "location.address",
        "location.latitude",
        "location.longitude",
      ];
    case FormStep.Media:
      return ["images"];
    case FormStep.Contact:
      return [
        "contactDetails.name",
        "contactDetails.phone",
        "contactDetails.email",
        "contactDetails.whatsapp",
        "contactDetails.botim",
        "contactDetails.hideContactUntilRequest",
        "authorizedToSell", // These are rendered in ContactStep
        "termsAccepted", // and are required for submission
      ];
    case FormStep.TitleDescription:
      return ["title", "description"];
    case FormStep.Review:
      // All fields are required for review
      return [];
    default:
      return [];
  }
};

export const mapFormToApiData = (formData: any) => {
  console.log("formData in mapFormToApiData:", formData); // Added for debugging
  console.log("Contact Details in mapFormToApiData:", formData.contactDetails);
  console.log("Features in mapFormToApiData:", formData.features);
  console.log("Amenities in mapFormToApiData:", formData.amenities);
  return {
    title: formData.title,
    description: formData.description,
    property_type_id: Number(formData.propertyType),
    listing_type: formData.listingType,
    price: Number(formData.price),
    currency: formData.currency,
    location_id:
      formData.location?.city_id &&
      typeof formData.location.city_id === "number" &&
      formData.location.city_id > 0
        ? formData.location.city_id
        : undefined, // Changed null to undefined
    address: formData.location?.address || "", // Changed to ensure empty string if empty for backend compatibility
    latitude: formData.location?.latitude,
    longitude: formData.location?.longitude,
    size: formData.size,
    size_unit: formData.size_unit,
    bedrooms: formData.bedrooms,
    bathrooms: formData.bathrooms,
    parking_spaces: formData.parkings,
    rooms: formData.rooms,
    num_floors: formData.floors,
    plot_size: formData.plotSizeSqft,
    floor_size: formData.floorSizeSqFt,
    furnishing: formData.furnishing,
    availability_status: formData.status,
    advance_payment_months: formData.advancePayment,
    minimum_term_months:
      formData.minimumTermUnit === "years"
        ? formData.minimumTermValue * 12
        : formData.minimumTermValue,
    deposit_months: formData.depositRequired,
    short_term_available: !!formData.shortTermAvailable, // Convert to boolean
    bills_included: !!formData.billsIncluded, // Convert to boolean
    yearBuilt: formData.yearBuilt, // Add yearBuilt to API data mapping
    // Add missing fields
    price_period: formData.pricePeriod,
    contact_name: formData.contactDetails?.name,
    contact_phone: formData.contactDetails?.phone,
    contact_email: formData.contactDetails?.email || undefined,
    contact_whatsapp: formData.contactDetails?.whatsapp,
    contact_botim: formData.contactDetails?.botim,
    hide_contact: formData.contactDetails?.hideContactUntilRequest,
    authorized_to_sell: !!formData.authorizedToSell, // Ensure boolean
    terms_accepted: !!formData.termsAccepted, // Ensure boolean
    attributes: formData.attributes || [], // Use formData.attributes directly
    cplk_ref: formData.cplk_ref, // Add cplk_ref
  };
};

export const mapListingToFormValues = (
  fetchedData: Listing
): ListingFormValues => {
  const listing = fetchedData.listing; // Extract the nested listing object
  if (!listing) {
    console.error(
      "[mapListingToFormValues] 'listing' object is missing from fetchedData:",
      fetchedData
    );
    return {} as ListingFormValues; // Return empty or default values if listing is missing
  }

  return {
    id: listing.id,
    title: listing.title || "",
    description: listing.description || "",
    propertyType: listing.property_type_id || undefined,
    propertyTypeDisplayName:
      fetchedData.propertyType?.display_name ||
      fetchedData.propertyType?.name ||
      "", // Use fetchedData.propertyType
    listingType: listing.listing_type || "sale", // Default to 'sale'
    price: listing.price || 0,
    currency: listing.currency || "LKR",
    pricePeriod: listing.price_period
      ? pricePeriodOptions.find(
          (option) =>
            option.toLowerCase() === listing.price_period?.toLowerCase()
        ) || ""
      : "",
    floorSizeSqFt: listing.floor_size || listing.size || undefined, // Use floor_size first, then size
    plotSizeSqft: listing.plot_size || undefined,
    rooms: listing.rooms || listing.bedrooms || undefined, // Use rooms first, then bedrooms
    bathrooms: listing.bathrooms || undefined,
    parkings: listing.parking_spaces || undefined,
    floors: listing.num_floors || undefined,
    furnishing: listing.furnishing || "",
    status: listing.availability_status || "",
    advancePayment: listing.advance_payment_months || undefined,
    minimumTermValue: listing.minimum_term_months || undefined,
    minimumTermUnit: listing.minimum_term_unit === "years" ? "years" : "months",
    depositRequired: listing.deposit_months || undefined,
    shortTermAvailable: !!listing.short_term_available,
    billsIncluded: !!listing.bills_included,
    location: {
      province: "", // These will be filled asynchronously in FormProvider
      district: "", // These will be filled asynchronously in FormProvider
      city: "", // These will be filled asynchronously in FormProvider
      address: listing.address || "",
      city_id: listing.city_id || undefined,
      latitude: listing.latitude || undefined,
      longitude: listing.longitude || undefined,
    },
    contactDetails: {
      name: listing.contact_name || "",
      phone: listing.contact_phone || "",
      email: listing.contact_email || "",
      whatsapp: !!listing.contact_whatsapp,
      botim: !!listing.contact_botim,
      hideContactUntilRequest: !!listing.hide_contact,
    },
    attributes: fetchedData.attributes?.map((attr) => attr.id) || [], // Use fetchedData.attributes
    authorizedToSell: !!listing.authorized_to_sell,
    termsAccepted: !!listing.terms_accepted,
  };
};
